'use client';

import { ALL_STATES } from '@/constants/location_states';
import {
  toCleanSlug,
  toInternalDistrictSlug,
  toInternalStateSlug,
} from '@/lib/location_slug_mapper';
import { getTodayISODate } from '@/lib/public_updates';
import { getTenantFromStateSlug } from '@/lib/tenant';
import { useLazyAllDistricts } from '@/hooks/useLazyAllDistricts';
import { useRouter } from 'next/navigation';
import { FormEvent, useMemo, useState } from 'react';

type PickerMode = 'jobs' | 'electricity';

interface UpdatesLocationPickerProps {
  mode: PickerMode;
  className?: string;
  initialStateSlug?: string;
  initialDistrictSlug?: string;
  initialDate?: string;
}

export default function UpdatesLocationPicker({
  mode,
  className,
  initialStateSlug = '',
  initialDistrictSlug = '',
  initialDate,
}: UpdatesLocationPickerProps) {
  const router = useRouter();
  const initialStateInternal = toInternalStateSlug(initialStateSlug) || '';
  const initialDistrictInternal =
    initialStateInternal && initialDistrictSlug
      ? toInternalDistrictSlug({
          stateInternalSlug: initialStateInternal,
          districtParam: initialDistrictSlug,
        }) || ''
      : '';
  const [stateSlug, setStateSlug] = useState(initialStateInternal);
  const [districtSlug, setDistrictSlug] = useState(initialDistrictInternal);
  const [date, setDate] = useState(initialDate || getTodayISODate());
  const allDistricts = useLazyAllDistricts();

  const stateOptions = useMemo(
    () =>
      Object.entries(ALL_STATES).map(([slug, state]) => ({
        slug,
        label: state.inline_label || state.label,
        code: state.value,
      })),
    [],
  );

  const selectedStateCode = stateOptions.find((option) => option.slug === stateSlug)?.code;
  const districtOptions = useMemo(() => {
    if (!selectedStateCode || !allDistricts) {
      return [];
    }
    const districtMap = allDistricts[selectedStateCode] || {};
    return Object.entries(districtMap).map(([slug, district]) => ({
      slug,
      label: district.label,
    }));
  }, [selectedStateCode, allDistricts]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stateSlug) {
      return;
    }
    const tenant = getTenantFromStateSlug(stateSlug);
    const cleanState = toCleanSlug(stateSlug);
    const cleanDistrict = districtSlug ? toCleanSlug(districtSlug) : '';
    if (mode === 'jobs') {
      if (cleanDistrict) {
        router.push(`/jobs/${cleanState}/${cleanDistrict}?tenant=${tenant}`);
        return;
      }
      router.push(`/jobs/${cleanState}?tenant=${tenant}`);
      return;
    }
    if (!cleanDistrict) {
      return;
    }
    router.push(`/electricity-updates/${cleanState}/${cleanDistrict}/${date}?tenant=${tenant}`);
  };

  return (
    <form
      onSubmit={onSubmit}
      className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${className || ''}`}
    >
      <div className='grid gap-3 md:grid-cols-4'>
        <select
          className='rounded-md border border-gray-300 px-3 py-2 text-sm'
          value={stateSlug}
          onChange={(event) => {
            setStateSlug(event.target.value);
            setDistrictSlug('');
          }}
        >
          <option value=''>Select state</option>
          {stateOptions.map((state) => (
            <option key={state.slug} value={state.slug}>
              {state.label}
            </option>
          ))}
        </select>
        <select
          className='rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100'
          value={districtSlug}
          disabled={!stateSlug}
          onChange={(event) => setDistrictSlug(event.target.value)}
        >
          <option value=''>Select district</option>
          {districtOptions.map((district) => (
            <option key={district.slug} value={district.slug}>
              {district.label}
            </option>
          ))}
        </select>
        {mode === 'electricity' ? (
          <input
            type='date'
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        ) : (
          <div className='hidden md:block' />
        )}
        <button
          type='submit'
          className='rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-400'
          disabled={!stateSlug || (mode === 'electricity' && !districtSlug)}
        >
          View updates
        </button>
      </div>
    </form>
  );
}
